import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Copy, Check, Lightbulb } from 'lucide-react';
import { getPrompts } from '../services/database';
import SuggestPromptModal from './SuggestPromptModal';

interface Prompt {
  id: string;
  title: string;
  description: string;
  prompt: string;
  category: string;
  tags: string[];
}

const PromptLibrary: React.FC = () => {
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(new Set());
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [showSuggestModal, setShowSuggestModal] = useState(false);

  // Load prompts from database
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const databasePrompts = await getPrompts();
        if (databasePrompts.length === 0) {
          // Fallback to sample prompts if no admin prompts exist
          const samplePrompts: Prompt[] = [
            {
              id: '1',
              title: 'Brand Voice Analysis',
              description: 'Analyse and refine brand voice for marketing materials',
              prompt: 'Please analyse the following brand voice and provide recommendations for improvement: [BRAND CONTENT]',
              category: 'Marketing',
              tags: ['brand', 'voice', 'analysis']
            },
            {
              id: '2',
              title: 'Competitive Research',
              description: 'Conduct comprehensive competitive analysis',
              prompt: 'Research the following competitors and provide insights on their strategies: [COMPETITOR LIST]',
              category: 'Strategy',
              tags: ['research', 'competitive', 'analysis']
            },
            {
              id: '3',
              title: 'Content Calendar Planning',
              description: 'Create a strategic content calendar',
              prompt: 'Help me create a content calendar for [TOPIC/INDUSTRY] with key themes and publishing schedule',
              category: 'Content',
              tags: ['calendar', 'planning', 'content']
            }
          ];
          setPrompts(samplePrompts);
        } else {
          setPrompts(databasePrompts);
        }
      } catch (error) {
        console.error('Error loading prompts:', error);
        // Fallback to sample prompts on error
        const samplePrompts: Prompt[] = [
          {
            id: '1',
            title: 'Brand Voice Analysis',
            description: 'Analyse and refine brand voice for marketing materials',
            prompt: 'Please analyse the following brand voice and provide recommendations for improvement: [BRAND CONTENT]',
            category: 'Marketing',
            tags: ['brand', 'voice', 'analysis']
          },
          {
            id: '2',
            title: 'Competitive Research',
            description: 'Conduct comprehensive competitive analysis',
            prompt: 'Research the following competitors and provide insights on their strategies: [COMPETITOR LIST]',
            category: 'Strategy',
            tags: ['research', 'competitive', 'analysis']
          },
          {
            id: '3',
            title: 'Content Calendar Planning',
            description: 'Create a strategic content calendar',
            prompt: 'Help me create a content calendar for [TOPIC/INDUSTRY] with key themes and publishing schedule',
            category: 'Content',
            tags: ['calendar', 'planning', 'content']
          }
        ];
        setPrompts(samplePrompts);
      }
    };

    loadPrompts();
  }, []);

  // Listen for changes to database
  useEffect(() => {
    // This useEffect is no longer needed as prompts are loaded from database
    // If you want to react to database changes, you'd need a different mechanism.
  }, []);

  const togglePrompt = (promptId: string) => {
    const newExpanded = new Set(expandedPrompts);
    if (newExpanded.has(promptId)) {
      newExpanded.delete(promptId);
    } else {
      newExpanded.add(promptId);
    }
    setExpandedPrompts(newExpanded);
  };

  const copyToClipboard = async (prompt: string, promptId: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPrompt(promptId);
      setTimeout(() => setCopiedPrompt(null), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-100 mb-2">Prompt Library</h2>
        <p className="text-gray-300">Curated prompts to help you get the most out of our GPT assistants</p>
        <button
          onClick={() => setShowSuggestModal(true)}
          className="mt-4 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2 mx-auto"
        >
          <Lightbulb className="w-5 h-5" />
          <span>Suggest a Prompt</span>
        </button>
      </div>

      <div className="space-y-6">
        {prompts.map((prompt) => (
          <div key={prompt.id} className="bg-[#21282D] rounded-lg border border-gray-600 overflow-hidden">
            <button
              onClick={() => togglePrompt(prompt.id)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-700 transition-colors duration-200"
            >
              <div className="flex-1 text-left">
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  {prompt.title}
                </h3>
                <p className="text-gray-300 text-sm mb-3">
                  {prompt.description}
                </p>
                <div className="flex items-center space-x-2">
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-red-900 text-red-300 rounded">
                    {prompt.category}
                  </span>
                  {prompt.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="inline-block px-2 py-1 text-xs font-medium bg-gray-600 text-gray-100 rounded">
                      {tag}
                    </span>
                  ))}
                  {prompt.tags.length > 2 && (
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-600 text-gray-100 rounded">
                      +{prompt.tags.length - 2}
                    </span>
                  )}
                </div>
              </div>
              {expandedPrompts.has(prompt.id) ? (
                <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
              )}
            </button>

            {expandedPrompts.has(prompt.id) && (
              <div className="px-6 pb-6">
                <div className="bg-[#1B2225] rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-100">Prompt:</h4>
                    <button
                      onClick={() => copyToClipboard(prompt.prompt, prompt.id)}
                      className="px-3 py-1 text-xs font-medium text-gray-300 hover:text-gray-100 transition-colors duration-200 flex items-center space-x-2"
                    >
                      {copiedPrompt === prompt.id ? (
                        <>
                          <Check className="w-3 h-3" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-[#21282D] rounded p-3 border border-gray-600">
                    <p className="text-gray-300 text-sm font-mono leading-relaxed">
                      {prompt.prompt}
                    </p>
                  </div>
                </div>
                
                <div className="text-xs text-gray-400">
                  <p>💡 <strong>Tip:</strong> Customise this prompt by replacing the placeholder text in brackets with your specific information.</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {prompts.length === 0 && (
        <div className="text-center py-8">
          <div className="bg-[#21282D] rounded-lg border border-gray-600 p-8">
            <h3 className="text-lg font-semibold text-gray-100 mb-4">
              No Prompts Available
            </h3>
            <p className="text-gray-300 mb-4">
              No prompts have been added to the library yet. Use the admin panel to add your first prompt.
            </p>
            <p className="text-gray-400 text-sm">
              Click the settings icon in the header to access the admin panel.
            </p>
          </div>
        </div>
      )}

      <div className="text-center py-8">
        <div className="bg-[#21282D] rounded-lg border border-gray-600 p-8">
          <h3 className="text-lg font-semibold text-gray-100 mb-4">
            Coming Soon
          </h3>
          <p className="text-gray-300 mb-4">
            We're working on expanding our prompt library with more categories and specialised prompts.
          </p>
          <p className="text-gray-400 text-sm">
            Have a great prompt idea? Let us know!
          </p>
        </div>
      </div>

      <SuggestPromptModal
        isVisible={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        onSuccess={() => {
          // Optionally reload prompts after successful submission
          // For now, we'll just close the modal
        }}
      />
    </div>
  );
};

export default PromptLibrary; 
